<template>
  <div class="agent-card" @click="handleClick">
    <div class="left">
      <img v-if="agent.avatarUrl" :src="agent.avatarUrl" class="agent-avatar-image" />
      <img v-else src="../assets/images/profile.svg" class="agent-avatar-image">
    </div>

    <div class="right">
      <div class="header-row">
        <h3>{{ agent.name.en }}</h3>
        <div class="status-badge" :class="statusClass">
          <div class="status-dot"></div>
          <span class="status-text">{{ statusText }}</span>
        </div>
      </div>
      <p>{{ agent.description || 'No description available' }}</p>
      <div class="agent-meta">
        <div class="agent-meta-wrapper">
        <span class="documents-count">{{ agent.documentsCount }} {{ $t('documents') }}</span>
        <span class="created-date">{{ formattedDate }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentCard',
  props: {
    agent: {
      type: Object,
      required: true
    },
    onClick: {
      type: Function,
      default: null
    }
  },
  computed: {
    formattedDate() {
      if (!this.agent.createdAt) return '';

      const date = new Date(this.agent.createdAt);
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

      if (diffInHours < 24) {
        return `${diffInHours} ${this.$t('time_hours')} ${this.$t('time_ago')}`;
      } else if (diffInHours < 24 * 7) {
        const days = Math.floor(diffInHours / 24);
        return `${days} ${this.$t('time_days')} ${this.$t('time_ago')}`;
      } else {
        const locale = this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US';
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
      }
    },

    statusClass() {
      const status = this.agent.status || 'draft';
      return `status-${status}`;
    },

    statusText() {
      const statusMap = {
        'draft': this.$t('ready'),
        'pending': this.$t('pending'),
        'training': this.$t('training'),
        'published': this.$t('published'),
        'failed': this.$t('failed'),
        'deleted': this.$t('deleted')
      };

      const status = this.agent.status || 'draft';
      return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
    }
  },
  methods: {
    handleClick() {
      if (this.onClick) {
        this.onClick(this.agent.token);
      } else {
        this.$router.push(`/agent/${this.agent.token}`);
      }
    }
  }
}
</script>

<style scoped>
.agent-card {
  border: 1px solid #EAECF0;
  background-color: #fff;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
}

.agent-card:hover {
  box-shadow: 0 4px 20px rgba(16, 24, 40, 0.08);
  transform: translateY(-2px);
}

.left {
  flex-shrink: 0;
}

.agent-avatar-image {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
}

.right {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 15px;
  min-height: 100%;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 12px;
}

.right h3 {
  font-size: 18px;
  color: #040C2D;
  margin: 0;
  font-family: euclid_medium, sans-serif;
  line-height: 1.2;
  flex: 1;
}

.right p {
  font-size: 14px;
  color: #667085;
  font-family: euclid_regular, sans-serif;
  margin-bottom: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.agent-meta {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  font-size: 12px;
  color: #98A2B3;
  font-family: euclid_regular, sans-serif;
  width: 100%;
  margin-top: auto;
}

.agent-meta-wrapper{
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.documents-count {
  background-color: #F2F4F7;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
}

/* Status Badge Styles */
.status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-family: euclid_medium, sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-text {
  font-size: 11px;
  font-family: euclid_medium, sans-serif;
}

/* Draft - Gray */
.status-draft {
  background-color: #F2F4F7;
  color: #667085;
}

.status-draft .status-dot {
  background-color: #667085;
}

/* Pending - Orange */
.status-pending {
  background-color: #FFFAEB;
  color: #B54708;
}

.status-pending .status-dot {
  background-color: #F79009;
}

/* Training - Blue */
.status-training {
  background-color: #EFF8FF;
  color: #175CD3;
}

.status-training .status-dot {
  background-color: #2E90FA;
}

/* Published - Green */
.status-published {
  background-color: #ECFDF3;
  color: #027A48;
}

.status-published .status-dot {
  background-color: #12B76A;
}

/* Failed - Red */
.status-failed {
  background-color: #FEF3F2;
  color: #B42318;
}

.status-failed .status-dot {
  background-color: #F04438;
}

/* Deleted - Dark Gray */
.status-deleted {
  background-color: #F9FAFB;
  color: #475467;
}

.status-deleted .status-dot {
  background-color: #98A2B3;
}

@media (max-width: 768px) {
  .agent-card {
    padding: 16px;
  }

  .header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .status-badge {
    align-self: flex-start;
  }

  .agent-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
